// TierRevealClip — crossfade + zoom "upgrade clip" when a facility or
// the club hero reaches a higher tier. Wraps any visual content and
// animates whenever `tierKey` increases.

import { useEffect, useRef, useState, type ReactNode } from 'react'
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion'
import { playSound, SOUND_IDS } from '@/systems/audioSystem'

interface TierRevealClipProps {
  /** Changes (upward) trigger the reveal animation */
  tierKey: number
  children: ReactNode
}

export function TierRevealClip({ tierKey, children }: TierRevealClipProps) {
  const previous = useRef(tierKey)
  const [revealing, setRevealing] = useState(false)
  const reduced = useReducedMotion()

  useEffect(() => {
    if (tierKey > previous.current) {
      previous.current = tierKey
      if (!reduced) {
        setRevealing(true)
        try { playSound(SOUND_IDS.BUILD) } catch { /* audio optional */ }
        const timer = setTimeout(() => setRevealing(false), 900)
        return () => clearTimeout(timer)
      }
    }
    previous.current = tierKey
  }, [tierKey, reduced])

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      <AnimatePresence mode="popLayout">
        <motion.div
          key={tierKey}
          initial={reduced ? { opacity: 0 } : { opacity: 0, scale: 0.94, filter: 'brightness(1.6)' }}
          animate={{ opacity: 1, scale: 1, filter: 'brightness(1)' }}
          exit={reduced ? { opacity: 0 } : { opacity: 0, scale: 1.06 }}
          transition={{ type: 'spring', stiffness: 180, damping: 22 }}
          style={{ width: '100%', height: '100%' }}
        >
          {children}
        </motion.div>
      </AnimatePresence>

      {/* Radial flash on upgrade */}
      <AnimatePresence>
        {revealing && (
          <motion.div
            initial={{ opacity: 0.9 }}
            animate={{ opacity: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
            style={{
              position: 'absolute',
              inset: 0,
              pointerEvents: 'none',
              background: 'radial-gradient(circle at center, rgba(255,255,255,0.75) 0%, transparent 65%)',
            }}
          />
        )}
      </AnimatePresence>
    </div>
  )
}
